import { normalizeClass, shallowReactive, watchPostEffect, normalizeStyle, h, render, Fragment, reactive, camelize } from 'vue';
import { stringifyStyle } from '@vue/shared';

const name = 'skeleton', preCls = `x-${name}`, itemPreCls = `${preCls}-item`, itemRefPrecls = `${itemPreCls}-ref`;

const map = new WeakMap();
const observer = new ResizeObserver((entries, ob) => {
    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i], handlers = map.get(entry.target);
        handlers === null || handlers === void 0 ? void 0 : handlers.forEach(e => e(entry, ob));
    }
});
var resizeobserver = {
    observer(el, handler) {
        if (handler) {
            const handlers = map.get(el);
            if (handlers)
                handlers.add(handler);
            else
                map.set(el, new Set([handler]));
        }
        observer.observe(el);
    },
    dispose(el, handler) {
        var _a;
        if (handler) {
            (_a = map.get(el)) === null || _a === void 0 ? void 0 : _a.delete(handler);
        }
        else {
            map.delete(el);
            observer.unobserve(el);
        }
    },
    unobserve(el) {
        observer.unobserve(el);
    }
};

const patchClass = (el, clazz, oldClazz) => {
    clazz = clazz.split(' '), oldClazz = oldClazz.split(' ');
    const delCls = oldClazz.filter(e => !clazz.includes(e)), addCls = clazz;
    delCls.forEach(e => e && el.classList.remove(e));
    addCls.forEach(e => e && el.classList.add(e));
};
const getLoading = (binding) => typeof binding.value === 'object' ? binding.value.loading : binding.value;
const getAnimated = (binding) => binding.modifiers.animated;
const getClass = (binding) => {
    const loading = getLoading(binding);
    if (!loading)
        return '';
    const animated = getAnimated(binding);
    // @ts-ignore
    return normalizeClass([preCls, { [`${preCls}-animated`]: animated }, binding.value.loadingClass]);
};
const allClass = (binding) => {
    // @ts-ignore
    return normalizeClass([preCls, `${preCls}-animated`, binding.value.loadingClass]);
};

const setup = (el, binding, vnode) => {
    const props = shallowReactive({
        el,
        binding,
        fragment: el.appendChild(document.createElement('div')),
        targets: shallowReactive([]),
        vnode,
    });
    el = null;
    binding = null;
    vnode = null;
    const unwatch1 = watchPostEffect(() => {
        var _a;
        const { el, binding, vnode } = props;
        if (getLoading(binding)) {
            patchClass(el, getClass(binding), allClass(binding));
            // @ts-ignore
            el.style.cssText = stringifyStyle(normalizeStyle([el.style.cssText, binding.value.loadingStyle]));
        }
        else {
            el.classList.remove(...allClass(binding).split(' '));
            el.style.cssText = stringifyStyle((_a = vnode.props) === null || _a === void 0 ? void 0 : _a.style);
        }
    });
    const unwatch2 = watchPostEffect(() => {
        const { binding, fragment } = props;
        const targets = props.targets, loading = getLoading(binding);
        // loading 状态才使用 resizeobserver
        if (loading)
            resizeobserver.observer(props.el);
        else
            resizeobserver.unobserve(props.el);
        if (loading) {
            targets.forEach(e => e.el.classList.add(itemRefPrecls));
        }
        else {
            targets.forEach(e => e.el.classList.remove(itemRefPrecls));
        }
        // 渲染 skeleton-item
        if (loading) {
            const items = targets.map(e => {
                var _a, _b;
                return h('div', {
                    class: [props.binding.value.class, (_a = e.binding.value) === null || _a === void 0 ? void 0 : _a.class, itemPreCls],
                    style: [props.binding.value.style, (_b = e.binding.value) === null || _b === void 0 ? void 0 : _b.style, e.state.style],
                });
            });
            render(h(Fragment, items), fragment);
        }
        else {
            render(null, fragment);
        }
    });
    props.unwatch = () => {
        unwatch1();
        unwatch2();
    };
    return props;
};
// —————————————————————————————— 华丽的分割线 ————————————————————————
const skeletonList = [];
const Skeleton = {
    created(el, binding, vnode) {
        skeletonList.push(setup(el, binding, vnode));
    },
    beforeMount(el, binding, vnode) {
        const props = skeletonList.find(e => e.el === el);
        props.binding = binding;
        props.vnode = vnode;
    },
    beforeUpdate(el, binding, vnode) {
        const props = skeletonList.find(e => e.el === el);
        props.vnode = vnode;
        props.binding = binding;
    },
    beforeUnmount(el) {
        const i = skeletonList.findIndex(e => e.el === el);
        if (i < 0)
            return;
        const props = skeletonList.splice(i, 1)[0];
        props.unwatch();
        props.fragment.parentNode.removeChild(props.fragment);
        resizeobserver.dispose(el);
    }
};

const SkeletonItem = {
    mounted(el, binding) {
        // @ts-ignore
        const props = skeletonList.findLast(e => e.el.contains(el));
        if (!props)
            return;
        const child = createItem(props, el, binding);
        props.targets.push(child);
    },
    beforeUnmount(el) {
        // @ts-ignore
        const props = skeletonList.findLast(e => e.el.contains(el));
        if (!props)
            return;
        const i = props.targets.findIndex(e => e.el === el);
        if (i < 0)
            return;
        const child = props.targets[i];
        props.targets.splice(i, 1);
        resizeobserver.dispose(props.el, child.resizeCb);
    }
};
const createItem = (props, el, binding) => {
    el.classList.add(`${itemPreCls}-ref`);
    const { el: parentEl } = props;
    const child = {
        el,
        binding,
        state: reactive({}),
        resizeCb: () => {
            const parentRect = parentEl.getBoundingClientRect(), parentStyle = getComputedStyle(parentEl);
            const style = getComputedStyle(el), rect = el.getBoundingClientRect();
            const _style = { position: 'absolute' };
            // 对其位置
            ['top', 'left'].forEach(e => {
                const borderWidth = parseInt(parentStyle[camelize(`border-${e}-width`)]);
                _style[e] = Math.abs(rect[e] - parentRect[e]) - borderWidth + 'px';
            });
            ['width', 'height'].forEach(e => _style[e] = Math.ceil(rect[e]) + 'px');
            _style.borderRadius = style.borderRadius;
            _style.zIndex = +style.zIndex + 1;
            child.state.style = _style;
        }
    };
    child.resizeCb();
    resizeobserver.observer(parentEl, child.resizeCb);
    return child;
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".x-skeleton{position:relative}.x-skeleton-item{background-color:#e6e6e6;box-sizing:border-box}.x-skeleton-item-ref{opacity:0;pointer-events:none;user-select:none}.x-skeleton-animated .x-skeleton-item{animation:x-skeleton-loading 1.4s ease infinite;background:linear-gradient(90deg,#f2f2f2 25%,#e6e6e6 37%,#f2f2f2 63%);background-size:400% 100%}@keyframes x-skeleton-loading{0%{background-position:100% 50%}to{background-position:0 50%}}";
styleInject(css_248z);

var index = {
    install(app) {
        app.directive(name, Skeleton);
        app.directive(`${name}-item`, SkeletonItem);
    }
};

export { index as default };
//# sourceMappingURL=index.esm.js.map
